import { withSafeTimeout } from '@hocs/safe-timers';
import withLifecycle from '@hocs/with-lifecycle';
import { MediaImageStudioMode } from '@wix/media-image-studio-opener/dist/src';
import { func, string } from 'prop-types';
import {
  all,
  always,
  any,
  assoc,
  complement,
  cond,
  contains,
  equals,
  filter,
  find,
  forEach,
  groupBy,
  head,
  isEmpty,
  last,
  map,
  not,
  partition,
  pick,
  pipe,
  pluck,
  propEq,
  reduce,
  reject,
  replace,
  T,
  uniq,
  unnest,
} from 'ramda';
import { contained, isArray, isString, noop } from 'ramda-adjunct';
import React, { MouseEvent } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { connect } from 'react-refetch';
import { withRouter } from 'react-router';
import {
  compose,
  setDisplayName,
  setPropTypes,
  withHandlers,
  withProps,
  withPropsOnChange,
  withStateHandlers,
} from 'recompose';
import { Subscription } from 'rxjs';
import { Key } from 'ts-keycode-enum';
import { Button, Loader, Popover, Text } from '../../../components/common';
import { PurchaseButtonWrapper } from '../../../components/common/ItemActionsRow/PurchaseButtonWrapper';
import { ActionName } from '../../../constants/Bi';
import { MediaType } from '../../../constants/MediaType';
import { Selection } from '../../../constants/Selection';
import { MainSource } from '../../../constants/Source';
import { withExpandedViewController } from '../../../containers/ExpandedView';
import {
  cantDownloadFileNotification,
  deleteForeverNotification,
  emptyTrashNotification,
  itemsRestoreNotification,
  moveToNotification,
  pasteCopiedFilesNotification,
  pasteCutFilesNotification,
  deleteForeverWithStorageDeltaNotification,
  undoRemoveNotification,
  urlCopiedNotification,
} from '../../../containers/FilesManager/notifications';
import { cantCopyHereNotification } from '../../../containers/FilesManager/notifications/cantCopyHereNotification';
import { cantMoveHereNotification } from '../../../containers/FilesManager/notifications/cantMoveHereNotification';
import { itemRenamedNotification } from '../../../containers/FilesManager/notifications/itemRenamedNotification';
import {
  prepareItemsBeforeSubmit,
  preparePathBeforeSubmit,
  shouldShowMediaTypesSelectForNamespace,
} from '../../../containers/FilesManager/utils';
import { convertToSubmitFolderId } from '../../../containers/FilesManager/utils/convertToSubmitFolderId';
import { BI_ERRORS, errorLogger } from '../../../services/loggers/errorLogger';
import { fedopsLogger } from '../../../services/loggers/fedopsLogger';
import { useAppEvents, EVENT_STORAGE_QUOTA_SERVER_ERROR } from '../../../services/appEvents';
import { parentConnection, ParentConnectionEvent } from '../../../services/parentConnection';
import { ModalName } from '../../../constants/ModalName';
import { UploadError } from '../../../constants/UploadError';
import { extractExplorableMediaCategoryIdAndOrientation } from '../../bi';
import { getId } from '../../common';
import { copyToClipboard } from '../../copyToClipboard';
import { onHotKeys } from '../../dom/domEvents';
import { ActionClickOrigin } from '../../hooks/bi/useTrackActions/useTrackActions.types';
import {
  ClickOrigin as PreviewClickOrigin,
  PreviewTarget,
} from '../../hooks/bi/useTrackPreviewActions/useTrackPreviewActions.types';
import { withDropOnSidebar } from '../../hooks/useDropOnSidebar';
import { withUploadQueueObservables } from '../../hooks/useFilesUploader/useFilesUploader';
import { useModalHandlersAndOptions, withOpenedModalName } from '../../hooks/useModal';
import { withNotificatorConsumer } from '../../hooks/useNotificator';
import {
  AsyncNotificationStatus,
  INotification,
  INotificatorContext,
} from '../../hooks/useNotificator/useNotificator.types';
import { withPurchaseMedia } from '../../hooks/usePurchaseMedia';
import { withScrollStream } from '../../hooks/useScrollStream';
import {
  MediaManagerSDKAnyItem,
  MediaManagerSDKAsyncOperation,
  MediaManagerSDKFile,
  MediaManagerSDKFileOrFolder,
  MediaManagerSDKFolder,
  MediaManagerSDKResponse,
} from '../../media-manager-sdk';
import {
  canCreateNewSubfolder,
  canItemBeAddedToFavorite,
  canItemBeBought,
  canItemBeRemovedFromFavorite,
  canPasteToItem,
  getCopyableFileUrl,
  getItemById,
  isFile,
  isFolder,
  isInTrash,
  isItemRemovable,
  isMediaFromWixItem,
  isPictureFile,
  isRootFolder,
  isTransparentVideoFile,
  isUnsplashItem,
  isVideoFile,
  validateFileName,
} from '../../media-manager-sdk/items';
import { getRootFolderBySource } from '../../media-manager-sdk/root-folder';
import { isAllSource } from '../../media-manager-sdk/sources';
import { isFailed, isTranscoding } from '../../media-manager-sdk/transcoding';
import { isMediaTypeEnabled, mediaTypesArrayToTranslatedArray } from '../../mediaType';
import { memoizedOver } from '../../memoize';
import { shouldKeepKeyBehaviorForModal } from '../../modals';
import { downloadItem } from '../../privateMedia/download';
import { canPasteItemsFromTo } from '../../privateMedia/folder';
import { RouteBuilder } from '../../RouteHelpers';
import { deserializeStringToArray } from '../../string';
import { uniqueFolderId } from '../../uniqueId';
import {
  getErrorInfo,
  getFailedFiles,
  getItemFromServerResponse,
  getNotAbortedFiles,
  getUploadedFiles,
} from '../../uploadingFile';
import { withItemsSubmittedAnalytics } from '../bi/withItemsSubmittedAnalytics';
import { ItemsSubmitType } from '../bi/withItemsSubmittedAnalytics/withItemsSubmittedAnalytics.types';
import { withPurchaseAnalytics } from '../bi/withPurchaseAnalytics';
import { withTrackActions } from '../bi/withTrackActions';
import { withTrackPreviewActions } from '../bi/withTrackPreviewActions';
import { ClickOrigin } from '../bi/withTrackSiteFilesAnalytics/withTrackSiteFilesAnalytics.types';
import { withStorageQuotaAwareHandler } from '../../hooks/storageQuota/useStorageQuotaAwareHandler';
import { StorageQuotaUseCase } from '../../hooks/storageQuota/useStorageQuotaAwareHandler/StorageQuotaUseCase';
import { FedOpsInteraction } from '../../../constants/FedOpsInteraction';
import { ejectIntl } from '../ejectIntl';
import { importUnsplashItem } from '../extended-sources/withUnsplash.utils';
import { memoizeHandlers } from '../memoizeHandlers';
import { withGetItemPrice } from '../withGetItemPrice';
import { JumpTo } from '../withItemsReducer/withItemsReducer.types';
import { withMMGRSettings, withoutMMGRSettings } from '../withMMGRSettings';
import { withoutRouter } from '../withoutRouter';
import { withSources } from '../withSources';
import { createHOCWithHookInside } from '../createHOCWithHookInside';
import { IWithItemsActionsInner, IWithItemsActionsOuter, ItemClickOptions } from './withItemsActions.types';
import { mimeTypesToNames } from '../../mimeTypes';
import styles from './withItemsActions.scss';
import { permanentDeleteSuccessNotification } from '../../../containers/FilesManager/notifications/permanentDeleteSuccessNotification';
import { HttpClient } from '@wix/http-client';
import { copyWixUrlToClipboard as copyWixUrlToClipboardUtil } from '../../copyWixUrlToClipboard';

const IMPORT_LOADER_DELAY = 200;

interface IAsyncOperation {
  itemIds: string[];
  notificationId: string;

  reopenNotification(): string;

  onDone?(props: any, notification: INotification, operation: this, extraData?: { [key: string]: number }): void;
}

interface IDestination {
  id: string;
  name: string;
}

interface IRestoreOperation extends IAsyncOperation {
  destinations: IDestination[];
}

const asyncOperations: Map<string, IAsyncOperation> = new Map();

const isOperationDone = (operation: IAsyncOperation): boolean => isEmpty(operation.itemIds);

export enum PreventSubscribeEventsNames {
  JumpToNextItemHotKey = 'jumpToNextItemHotKey',
  JumpToPrevItemHotKey = 'jumpToPrevItemHotKey',
  JumpToBottomItemHotKey = 'jumpToBottomItemHotKey',
  JumpToTopItemHotKey = 'jumpToTopItemHotKey',
  SelectAllItemsHotKey = 'selectAllItemsHotKey',
}

interface IExternalParams {
  preventSubscribeEvents?: PreventSubscribeEventsNames[];
}

export const withItemsActions = ({ preventSubscribeEvents }: IExternalParams = { preventSubscribeEvents: [] }) =>
  compose<IWithItemsActionsInner, IWithItemsActionsOuter>(
    withGetItemPrice,
    withStorageQuotaAwareHandler,
    createHOCWithHookInside(
      'withStorageFullModalHandlersAndOptions',
      useModalHandlersAndOptions,
      () => ModalName.StorageFull,
      ({ openModal: showStorageFullModal }) => ({ showStorageFullModal }),
    ),
    createHOCWithHookInside('withAppEvents', useAppEvents),
    setPropTypes({
      navigateTo: func.isRequired,
      getItemPrice: func.isRequired,
      fileType: string.isRequired,
    }),
    setDisplayName('withItemsActions'),
    withTrackPreviewActions,
    withPurchaseAnalytics,
    withStateHandlers(
      {
        createFolderOrigin: null,
        isImportInProgress: false,
      },
      {
        setCreateFolderOrigin: () => (origin) => ({ createFolderOrigin: origin }),
        startImport: () => () => ({ isImportInProgress: true }),
        stopImport: () => () => ({ isImportInProgress: false }),
      },
    ),
    withNotificatorConsumer,
    withSources,
    withMMGRSettings,
    withProps(({ cutItems, copiedItems, mmgrSettings: { isSecureContext } }) => ({
      isSecure: isSecureContext,
      hasCutItems: not(isEmpty(cutItems)),
      hasCopiedItems: not(isEmpty(copiedItems)),
    })),
    withHandlers({
      onCopyItemEvent: (props: INotificatorContext) => (data) => {
        const { getNotificationById } = props;

        const { opId, opItemId } = data.info;
        const operation = asyncOperations.get(opId);

        if (operation) {
          operation.itemIds = reject(equals(opItemId), operation.itemIds);

          if (isOperationDone(operation)) {
            const notification: INotification =
              getNotificationById(operation.notificationId) || getNotificationById(operation.reopenNotification());

            notification.setStatus(AsyncNotificationStatus.Finished);
            if (operation.onDone) {
              operation.onDone(props, notification, operation);
            }
            asyncOperations.delete(opId);
          }
        }
      },
      onRestoreItemEvent: (props: INotificatorContext) => (data) => {
        const { getNotificationById } = props;
        const { opId, data: result } = data.info;

        const restoredItemIds = map<{ fileName: string; folderId: string }, string>((item) => {
          return item.fileName || item.folderId;
        }, result);

        const operation = asyncOperations.get(opId) as IRestoreOperation;

        if (operation) {
          operation.itemIds = reject(contained(restoredItemIds), operation.itemIds);

          const newDestinations = map<{ parentFolderId: string; parentFolderName: string }, IDestination>((item) => {
            return { id: item.parentFolderId, name: item.parentFolderName };
          }, result);

          operation.destinations = uniq([...(operation.destinations || []), ...newDestinations]);

          if (isOperationDone(operation)) {
            const notification: INotification = operation.notificationId
              ? getNotificationById(operation.notificationId) || getNotificationById(operation.reopenNotification())
              : null;

            if (notification) {
              notification.setStatus(AsyncNotificationStatus.Finished);
            }

            if (operation.onDone) {
              operation.onDone(props, notification, operation);
            }

            asyncOperations.delete(opId);
          }
        }
      },
      onEmptyTrashNotification:
        (props: INotificatorContext) => (opId: string, usageDelta?: number, filesDeleted?: number) => {
          const { getNotificationById } = props;

          const operation = asyncOperations.get(opId);

          if (operation) {
            const notification: INotification =
              getNotificationById(operation.notificationId) || getNotificationById(operation.reopenNotification());

            if (usageDelta) {
              notification.setMessage(deleteForeverWithStorageDeltaNotification(usageDelta));
            }

            if (filesDeleted) {
              notification.setMessage(permanentDeleteSuccessNotification({ itemsCount: filesDeleted }));
            }

            notification.setStatus(AsyncNotificationStatus.Finished);
            if (operation.onDone) {
              operation.onDone(props, notification, operation, { filesDeleted, usageDelta });
            }
            asyncOperations.delete(opId);
          }
        },
    }),
    withTrackActions,
    withHandlers({
      trackActionsMenuClick:
        ({ trackActionsMenuClick, selectedItems, currentFolder, editingItem }) =>
        (clickOrigin, actionName) =>
          trackActionsMenuClick(
            isEmpty(selectedItems) ? [editingItem || currentFolder] : selectedItems,
            clickOrigin,
            actionName,
          ),
      trackActionsDone:
        ({ trackActionsDone, selectedItems, currentFolder, editingItem }) =>
        (actionName) =>
          trackActionsDone(isEmpty(selectedItems) ? [editingItem || currentFolder] : selectedItems, actionName),
    }),
    withHandlers({
      editSelectedImage:
        ({ editImage, selectedItems }) =>
        () =>
          editImage({ file: selectedItems[0], mode: MediaImageStudioMode.Transform }),
      beginDrag:
        ({ selectedItems }) =>
        (item) => ({
          draggingItems: contains(item, selectedItems) ? selectedItems : [item],
        }),
      handleEditItemCancel:
        ({ editingItem, selectItem, editItemCancel, createNewFolderCancel }) =>
        () => {
          const { id } = editingItem;

          if (editingItem.isNew) {
            createNewFolderCancel();
          } else {
            editItemCancel();
            selectItem(id);
          }
        },
    }),
    connect<any>(
      ({
        editingItem,
        mediaType,
        currentFolder,
        removeItems,
        createNewFolderComplete,
        moveItemsToTrashMP,
        restoreItemsFromTrashMP,
        emptyTrashMP,
        deleteForeverMP,
        updateItemMP,
        createNewFolderMP,
        updateLocalItem,
        getDownloadUrlMP,
        moveItemsMP,
        isInExpandedMode,
        trackFolderCreated,
        createFolderOrigin,
        trackActionsDone,
        openJustCreatedFolder,
        notify,
      }) => ({
        moveItemsToTrash: (items: MediaManagerSDKAnyItem[]) => {
          if (!items) {
            return {};
          }

          return {
            moveItemsToTrashRequest: {
              force: true,
              comparison: '',
              value: () => {
                const itemsToRemove = reject(isInTrash, items);
                return moveItemsToTrashMP(itemsToRemove);
              },
              andThen: () => {
                forEach((item: MediaManagerSDKAnyItem) => {
                  parentConnection.emit(ParentConnectionEvent.ItemRemoved, {
                    id: item.id,
                    source: item.source,
                  });
                }, items);

                trackActionsDone(ActionName.MoveToTrash);

                notify(undoRemoveNotification({ items }));
                removeItems(items, isInExpandedMode);

                return {};
              },
            },
          };
        },
        restoreItems: (
          items: MediaManagerSDKAnyItem[],
          callback: (response: MediaManagerSDKResponse<MediaManagerSDKAsyncOperation>) => void = noop,
        ) => {
          const [files, folders] = partition(isFile, items);

          return {
            restoreItemsRequest: {
              force: true,
              comparison: '',
              value: () => restoreItemsFromTrashMP(files, folders),
              andThen: (response: any) => {
                callback(response);
                return {};
              },
            },
          };
        },
        emptyTrash: (callback: (response: MediaManagerSDKResponse<MediaManagerSDKAsyncOperation>) => void = noop) => ({
          emptyTrashRequest: {
            force: true,
            comparison: '',
            value: () => emptyTrashMP(),
            andThen: (response: any) => {
              callback(response);
              return {};
            },
          },
        }),
        deleteForever: (
          items: MediaManagerSDKAnyItem[],
          callback: (response: MediaManagerSDKResponse<MediaManagerSDKAsyncOperation>) => void = noop,
        ) => {
          const [files, folders] = partition(isFile, items);

          return {
            deleteForeverRequest: {
              force: true,
              comparison: '',
              value: () => deleteForeverMP(files, folders),
              andThen: (response: any) => {
                callback(response);
                return {};
              },
            },
          };
        },
        startDownloadItem: (item) => ({
          downloadUrlRequest: {
            force: true,
            value: getDownloadUrlMP(item),
            andThen: (response: any) => {
              downloadItem(response.data.urlPath);
              trackActionsDone(ActionName.Download);
              return {};
            },
            andCatch: () => {
              notify(cantDownloadFileNotification());
              return {};
            },
          },
        }),
        updateItem: (
          item: MediaManagerSDKFileOrFolder,
          newProps: Partial<MediaManagerSDKFileOrFolder>,
          callback: Function = noop,
        ) => {
          if (!editingItem) {
            updateLocalItem(item.id, newProps);
          }

          return {
            updateItemRequest: {
              force: true,
              value: updateItemMP(item, newProps, { siteMediaToken: item.siteMediaToken }),
              andThen: () => {
                if (newProps.name) {
                  notify(itemRenamedNotification({ name: newProps.name }));
                }

                callback();
                return {};
              },
            },
          };
        },
        moveItems: (items, folder, callback: Function = noop) => ({
          updateItemRequest: {
            force: true,
            value: moveItemsMP(items, folder),
            andThen: () => {
              callback();
              return {};
            },
          },
        }),
        createNewFolder: (name) => ({
          createNewFolderRequest: {
            force: true,
            value: createNewFolderMP({ name, mediaType, parentId: currentFolder.id }),
            andThen: ({ data }) => {
              trackFolderCreated(data.id, createFolderOrigin);
              createNewFolderComplete(data);
              openJustCreatedFolder(editingItem.id, data);

              return {};
            },
          },
        }),
      }),
    ),
    memoizeHandlers(
      'moveItemsToTrash',
      'restoreItems',
      'emptyTrash',
      'deleteForever',
      'startDownloadItem',
      'updateItem',
      'moveItems',
      'createNewFolder',
    ),
    withHandlers({
      createNewFolderStart:
        ({ trackFolderCreateStart, createNewFolderStart, setCreateFolderOrigin }) =>
        (origin: string) => {
          const id = uniqueFolderId();

          trackFolderCreateStart(origin);
          setCreateFolderOrigin(origin);
          createNewFolderStart(id);
        },
      getPublicItemsInfo:
        ({ publicItemsInfoMP }) =>
        (itemIds) =>
          itemIds.length === 0 ? Promise.resolve([]) : publicItemsInfoMP(itemIds).then((response) => response.data),
      setLayout:
        ({ setLayout, trackActionsDone }) =>
        (layout) => {
          setLayout(layout);
          trackActionsDone(ActionName.LayoutChange);
        },
      setSort:
        ({ setSort, trackActionsDone }) =>
        (sort) => {
          setSort(sort);
          trackActionsDone(ActionName.SortChange);
        },
    }),
    withItemsSubmittedAnalytics,
    injectIntl,
    withMMGRSettings,
    withPropsOnChange(
      ['source', 'folderId', 'searchQuery', 'getPublicMediaSubcategoryNameAndRoot', 'openFolderById'],
      ({
        intl: { formatMessage },
        source,
        folderId,
        isMediaFromWix,
        isShutterstock,
        searchQuery,
        getPublicMediaSubcategoryNameAndRoot,
        openFolderById,
      }) => {
        const [categoryId, orientation] = extractExplorableMediaCategoryIdAndOrientation(source, folderId);
        const [publicMediaCategoryName] = isMediaFromWix
          ? getPublicMediaSubcategoryNameAndRoot(categoryId)
          : [null, null];

        const category = categoryId
          ? isShutterstock
            ? formatMessage({ id: `shutterstock.category.${categoryId}` })
            : publicMediaCategoryName
          : null;

        return {
          trackItemsSubmittedAnalyticsCommonProps: {
            category,
            categoryId,
            orientation,
            searchTerm: searchQuery || null,
            openFolderById,
          },
        };
      },
    ),
    withHandlers({
      createNewFolderStartFromTop:
        ({ createNewFolderStart }) =>
        () =>
          createNewFolderStart(ClickOrigin.TopRight),
      moveItems:
        ({ removeItems, currentFolder, isInExpandedMode, moveItems }) =>
        (items: MediaManagerSDKAnyItem[], folder: MediaManagerSDKFolder, callback = noop) => {
          const folderId = folder.id;

          removeItems(
            filter(
              (item) =>
                isFile(item)
                  ? item.folderId === currentFolder.id && item.folderId !== folderId
                  : item.parentId === currentFolder.id && item.parentId !== folderId,
              items,
            ),
            isInExpandedMode,
          );

          moveItems(items, folder, callback);
        },
      copyItems:
        ({ copyItemsMP, intl: { formatMessage } }) =>
        (items: MediaManagerSDKAnyItem[], destination: MediaManagerSDKFolder) =>
          copyItemsMP(
            map(
              (item) => ({
                ...item,
                name: formatMessage({ id: 'files-manager.copied-file-name' }, { itemName: item.name }),
              }),
              items,
            ),
            destination,
            { isFromG6: true },
          ),
      copyFileUrlToClipboard:
        ({ selectedItems, mmgrSettings, notify, trackActionsDone }) =>
        () => {
          const copyableUrl = getCopyableFileUrl(head(selectedItems), mmgrSettings.privateMediaRestVideoFiles);
          copyToClipboard(copyableUrl);
          notify(urlCopiedNotification());
          trackActionsDone(ActionName.CopyUrl);
        },
      copyWixUrlToClipboard:
        ({ selectedItems, notify, trackActionsDone }) =>
        async (httpClient: HttpClient) => {
          return copyWixUrlToClipboardUtil(httpClient, selectedItems, { notify, trackActionsDone });
        },
      trackMediaManagerFileChosen:
        ({ trackMediaManagerFileChosen, trackItemsSubmittedAnalyticsCommonProps, selectedItemTypes }) =>
        (items: MediaManagerSDKFile[], target: ItemsSubmitType) => {
          trackMediaManagerFileChosen({
            items,
            target,
            selectedItemTypes,
            ...trackItemsSubmittedAnalyticsCommonProps,
          });
        },
    }),
    withProps(
      ({
        isSingleItemSelected,
        selectedItems,
        isExplorableMedia,
        isRecommended,
        isShutterstock,
        isMediaFromWix,
        isTrash,
        isSearch,
        isBoardsRoot,
        isSecure,
        hasSelectedItems,
        hasNoItems,
        currentFolder,
        mmgrSettings: { namespace },
        isHome,
        unsplashDisclaimer,
        layouts,
      }) => {
        const isActionBarVisible = !isExplorableMedia;

        const isNestedFolderAndNoItemsSelected = !isRootFolder(currentFolder) && !hasSelectedItems;

        return {
          isSingleFolderSelected: isSingleItemSelected && isFolder(selectedItems[0]),
          isActionBarVisible,
          isSortSelectVisible: !isExplorableMedia && !isSearch && !hasNoItems,
          isMediaTypeSelectVisible:
            isActionBarVisible &&
            !isSearch &&
            !isTrash &&
            !isBoardsRoot &&
            shouldShowMediaTypesSelectForNamespace(namespace) &&
            !hasNoItems,
          isLayoutSwitchVisible: !isRecommended && !isHome && !isBoardsRoot && layouts.length > 1 && !hasNoItems,
          isSingleActionRowVisible: (isShutterstock || isMediaFromWix) && !isSearch,
          isCreateFolderVisible: canCreateNewSubfolder(currentFolder),
          isMoreActionsVisible: hasSelectedItems,
          isAddToFavoritesVisible:
            !isSecure &&
            ((hasSelectedItems && any(canItemBeAddedToFavorite, selectedItems)) ||
              (isNestedFolderAndNoItemsSelected && canItemBeAddedToFavorite(currentFolder))),
          isRemoveFromFavoritesVisible:
            !isSecure &&
            ((hasSelectedItems && all(canItemBeRemovedFromFavorite, selectedItems)) ||
              (isNestedFolderAndNoItemsSelected && canItemBeRemovedFromFavorite(currentFolder))),
          isRestoreVisible: isTrash && hasSelectedItems,
          isDeleteForeverVisible: isTrash && hasSelectedItems,
          isEmptyTrashVisible: isTrash && !hasNoItems && !hasSelectedItems,
          shouldHideInfoPanel: isExplorableMedia,
          footerDisclaimer: hasSelectedItems && unsplashDisclaimer,
        };
      },
    ),
    withHandlers({
      emptyTrash:
        ({ emptyTrash, notify, trackActionsDone }) =>
        () => {
          const showNotification = () => notify(emptyTrashNotification(), { isAsync: true });

          const { id: notificationId } = showNotification();

          emptyTrash((response) => {
            asyncOperations.set(response.data.opId, {
              notificationId,
              itemIds: [],
              reopenNotification: showNotification,
              onDone: ({ refreshFolder }) => {
                refreshFolder();
                trackActionsDone(ActionName.EmptyTrash);
              },
            });
          });
        },
      deleteForever:
        ({ deleteForever, notify, selectedItems, trackActionsDone }) =>
        () => {
          const showNotification = () => notify(deleteForeverNotification({ items: selectedItems }), { isAsync: true });

          const { id: notificationId } = showNotification();

          deleteForever(selectedItems, (response) => {
            asyncOperations.set(response.data.opId, {
              notificationId,
              itemIds: pluck('id', selectedItems) as string[],
              reopenNotification: showNotification,
              onDone: ({ removeItems, isInExpandedMode }) => {
                trackActionsDone(ActionName.DeleteForever);
                removeItems(selectedItems, isInExpandedMode);
              },
            });
          });
        },
      deleteForeverOperationCompleteSubscriber:
        ({ notify, items, trackPermanentDeleteActionDone }) =>
        ({ operationId, deletedItemIds, deletedFilesCount, hasIndirectFilesSelected, isUsedFiles }) => {
          const deletedVisibleItems = items.filter((file) => deletedItemIds.has(file.id));
          const itemName = deletedVisibleItems?.[0]?.name;
          const showNotification = () =>
            notify(
              permanentDeleteSuccessNotification({
                itemName,
                itemsCount: deletedFilesCount,
                // we know that something can be deleted, but not sure until duplexer event arrives.
                // So, only notify if confirming duplexer message arrives with non zero items count
                isAsyncSourcedFilesCount: hasIndirectFilesSelected,
              }),
              {
                isAsync: true,
                persist: true,
                increasedNotificationsSlots: true,
              },
            );
          const { id: notificationId } = showNotification();
          asyncOperations.set(operationId, {
            notificationId,
            itemIds: pluck('id', deletedVisibleItems) as string[],
            reopenNotification: showNotification,
            onDone: ({ refreshFolder, removeItems, isInExpandedMode }, _, __, extraData) => {
              trackPermanentDeleteActionDone(
                isUsedFiles ? ActionName.DeleteForeverUsed : ActionName.DeleteForeverUnused,
                deletedVisibleItems,
              );
              if (extraData.filesDeleted > 0) {
                if (deletedVisibleItems.length) {
                  removeItems(deletedVisibleItems, isInExpandedMode);
                } else {
                  refreshFolder();
                }
              }
            },
          });
        },
      pasteToFolder:
        ({
          sourceFilesFolderPath,
          hasCutItems,
          cutItems,
          notify,
          moveItems,
          openFolder,
          refreshFolder,
          setCutItems,
          copyItems,
          hasCopiedItems,
          copiedItems,
          isSingleFolderSelected,
          trackActionsDone,
          preserveOriginAndTargetAfterDone,
          clearOriginAndTarget,
          showStorageFullModal,
          appEvents,
        }) =>
        (toFolder, toFolderName, targetFolderPath) => {
          if (!canPasteToItem(toFolder)) {
            return;
          }

          const pasteActionName = isSingleFolderSelected ? ActionName.PasteHere : ActionName.Paste;
          const sourceFilesFolderName = sourceFilesFolderPath ? last(sourceFilesFolderPath).name : null;

          if (hasCutItems) {
            if (!canPasteItemsFromTo(targetFolderPath, cutItems)) {
              notify(cantMoveHereNotification());
              return;
            }

            trackActionsDone(pasteActionName);
            preserveOriginAndTargetAfterDone();

            moveItems(cutItems, toFolder, () => {
              notify(
                pasteCutFilesNotification({
                  items: cutItems,
                  sourceName: sourceFilesFolderName,
                  targetName: toFolderName,
                  onSourceClick() {
                    openFolder(last(sourceFilesFolderPath));
                  },
                  onTargetClick() {
                    openFolder(last(targetFolderPath));
                  },
                }),
              );

              refreshFolder();

              trackActionsDone(ActionName.Cut);
              clearOriginAndTarget();
            });

            setCutItems([]);
          } else if (hasCopiedItems) {
            if (!canPasteItemsFromTo(targetFolderPath, copiedItems)) {
              notify(cantCopyHereNotification());
              return;
            }

            const showNotification = () =>
              notify(
                pasteCopiedFilesNotification({
                  items: copiedItems,
                  sourceName: sourceFilesFolderName,
                  targetName: toFolderName,
                  onSourceClick() {
                    openFolder(last(sourceFilesFolderPath));
                  },
                  onTargetClick() {
                    openFolder(last(targetFolderPath));
                  },
                }),
                {
                  isAsync: true,
                },
              );

            const notification = showNotification();

            trackActionsDone(pasteActionName);
            preserveOriginAndTargetAfterDone();

            copyItems(copiedItems, toFolder)
              .then((response: { data: { opId: string } }) => {
                asyncOperations.set(response.data.opId, {
                  notificationId: notification.id,
                  itemIds: map(getId, copiedItems),
                  reopenNotification: showNotification,
                  onDone: ({ refreshFolder }) => {
                    refreshFolder();

                    trackActionsDone(ActionName.Copy);
                    clearOriginAndTarget();
                  },
                });
              })
              .catch((err: any) => {
                if (getErrorInfo(err).key === UploadError.QuotaTotalStorageExceeded) {
                  showStorageFullModal(StorageQuotaUseCase.COPY);
                  appEvents.emit(EVENT_STORAGE_QUOTA_SERVER_ERROR);
                  notification.dismiss();
                }
              });
          }
        },
    }),
    withHandlers({
      pasteToSelectedFolder:
        ({ pasteToFolder, selectedItems, folderPath }) =>
        () =>
          pasteToFolder(selectedItems[0], selectedItems[0].name, [...folderPath, selectedItems[0]]),
      pasteToCurrentFolder:
        ({ pasteToFolder, currentFolder, folderPath }) =>
        () =>
          pasteToFolder(currentFolder, currentFolder.name, folderPath),
    }),
    withHandlers({
      pasteItems: ({ isSingleFolderSelected, pasteToSelectedFolder, pasteToCurrentFolder, isFavorites }) =>
        isFavorites
          ? // disable paste in favorites
            noop
          : isSingleFolderSelected
          ? pasteToSelectedFolder
          : pasteToCurrentFolder,
      cutSelectedItems: ({ selectedItems, setCutItems, folderPath, isFavorites }) =>
        isFavorites
          ? // disable cut in favorites
            noop
          : () => setCutItems(filter(isItemRemovable, selectedItems), folderPath),
      copySelectedItems: ({ selectedItems, setCopiedItems, folderPath, storageQuotaAwareHandler }) =>
        storageQuotaAwareHandler(() => setCopiedItems(selectedItems, folderPath), StorageQuotaUseCase.COPY),
      isItemCut:
        ({ cutItems }) =>
        (itemId) =>
          Boolean(find(propEq('id', itemId), cutItems)),
      moveItemsAndNotify:
        ({ moveItems, notify, openFolder }) =>
        (items: MediaManagerSDKAnyItem[], destination: MediaManagerSDKFolder) => {
          moveItems(items, destination);
          notify(
            moveToNotification({
              items,
              targetName: destination.name,
              onTargetClick() {
                openFolder(destination);
              },
            }),
          );
        },
    }),
    withHandlers({
      moveSelectedItems:
        ({ moveItemsAndNotify, selectedItems }) =>
        (destination: MediaManagerSDKFolder) =>
          moveItemsAndNotify(selectedItems, destination),
      onUndoRemove:
        ({ restoreItems, trackActionsDone }) =>
        (items, notification) =>
        () => {
          notification.dismiss();

          const itemsToRestore = map(assoc('isInTrash', true), items);

          restoreItems(itemsToRestore, (response) => {
            asyncOperations.set(response.data.opId, {
              notificationId: null,
              itemIds: pluck('id', itemsToRestore),
              reopenNotification: null,
              onDone: ({ refreshFolder }) => {
                refreshFolder();
                trackActionsDone(ActionName.Restore);
              },
            });
          });
        },
      onUndoAddToFavorites:
        ({ removeItemsFromFavorites }) =>
        (items, notification) =>
        () => {
          notification.dismiss();
          removeItemsFromFavorites(items, { clickOrigin: ActionClickOrigin.Notification }, false);
        },
      onUndoRemoveFromFavorites:
        ({ addItemsToFavorites }) =>
        (items, notification) =>
        () => {
          notification.dismiss();
          addItemsToFavorites(items, { clickOrigin: ActionClickOrigin.Notification }, false);
        },
    }),
    withHandlers({
      restoreItems:
        ({ restoreItems, hasSelectedItems, selectedItems, notify, trackActionsDone, intl: { formatMessage } }) =>
        () => {
          if (hasSelectedItems) {
            const showNotification = () =>
              notify(itemsRestoreNotification({ items: selectedItems }), { isAsync: true });

            const { id: notificationId } = showNotification();

            restoreItems(selectedItems, (response) => {
              const operation: IRestoreOperation = {
                notificationId,
                itemIds: map(getId, selectedItems),
                reopenNotification: showNotification,
                destinations: [],
                onDone: (
                  { isTrash, refreshFolder, isInExpandedMode, removeItems, openFolderById, mmgrSettings },
                  notification,
                  operation,
                ) => {
                  if (operation.destinations.length === 1) {
                    const [destination] = operation.destinations;
                    const rootFolder = getRootFolderBySource(MainSource.SiteFiles, formatMessage, mmgrSettings);

                    if (destination.id === rootFolder.id) {
                      destination.id = rootFolder.id;
                      destination.name = rootFolder.name;
                    }

                    notification.setMessage(
                      itemsRestoreNotification({
                        items: selectedItems,
                        targetName: destination.name,
                        onTargetClick() {
                          openFolderById(destination.id, MainSource.SiteFiles);
                        },
                      }),
                    );
                  }

                  if (isTrash) {
                    removeItems(selectedItems, isInExpandedMode);
                  } else {
                    refreshFolder();
                  }
                },
              };

              asyncOperations.set(response.data.opId, operation);
              trackActionsDone(ActionName.Restore);
            });
          }
        },
      findItem:
        ({ items }) =>
        (itemId) =>
          getItemById(itemId, items),
      clearItemsSelection:
        ({ clearItemsSelection, openFolderById, currentFolder, searchQuery, source }) =>
        () => {
          clearItemsSelection();
          // when clearing items selection, need also to remove selected item id from url:
          // {source}/root/file-id => {source}/root

          // skip reopening folder if search query is present to avoid retriggering search
          if (!searchQuery) {
            openFolderById(currentFolder.id, source, searchQuery);
          }
        },
    }),
    withStateHandlers(
      { canScrollToUploadedItem: false },
      { setCanScrollToUploadedItem: () => (canScrollToUploadedItem) => ({ canScrollToUploadedItem }) },
    ),
    withHandlers({
      handleUploadQueueStart:
        ({ setCanScrollToUploadedItem }) =>
        () =>
          setCanScrollToUploadedItem(true),
      handleUploadQueueFinished:
        ({ items, selectItem, selectedItems, trackActionsDone }) =>
        (queue) => {
          if (isEmpty(queue)) {
            return;
          }

          const notAbortedFilesCount = getNotAbortedFiles(queue).length;
          const failedFilesCount = getFailedFiles(queue).length;
          const uploadedFilesCount = getUploadedFiles(queue).length;

          const isFileEditedInMediaStudio =
            notAbortedFilesCount === 1 && queue[0].options.isFromMediaStudio && failedFilesCount === 0;

          if (isFileEditedInMediaStudio) {
            trackActionsDone(ActionName.CropEdit);
          } else {
            trackActionsDone(ActionName.AddFilesToFolder);
          }

          const firstFileId = pipe(find(isFile), getId)(items);

          if (!firstFileId) {
            // no files in list
            return;
          }

          if (isFileEditedInMediaStudio) {
            selectItem(firstFileId);
          } else if (uploadedFilesCount && isEmpty(selectedItems)) {
            selectItem(firstFileId);
          }
        },

      handleItemClick:
        ({ selectItem, toggleItemSelection, selectItemsUntil, isItemEditing }) =>
        (item, event: MouseEvent<HTMLElement>, options: ItemClickOptions = {}) => {
          const { id } = item;
          const { ctrlKey, metaKey, shiftKey } = event;

          if (ctrlKey || metaKey) {
            toggleItemSelection(id, options);
          } else if (shiftKey) {
            selectItemsUntil(id, options);
          } else if (!isItemEditing(id)) {
            selectItem(id, options);
          }
        },
      handleItemRightClick:
        ({ isItemSelected, selectItem }) =>
        (item, _event, options: ItemClickOptions = {}) => {
          const { id } = item;

          if (!isItemSelected(id)) {
            selectItem(id, options);
          }
        },
      handleEditItemStart:
        ({ selectedItems, editItemStart, clearItemsSelection }) =>
        (itemId) => {
          const id = isString(itemId) ? itemId : selectedItems[0].id;

          editItemStart(id);
          clearItemsSelection();
        },
      handleNameChange:
        ({ updateEditingItem }) =>
        (name) =>
          updateEditingItem({ name }),
      handleEditItemComplete:
        ({
          editingItem,
          findItem,
          updateItem,
          selectItem,
          editItemComplete,
          createNewFolder,
          handleEditItemCancel,
          trackActionsDone,
        }) =>
        () => {
          const { id, name } = editingItem;
          const validatedFileName = validateFileName(name);

          if (!validatedFileName) {
            handleEditItemCancel();
            return;
          }

          if (editingItem.isNew) {
            createNewFolder(validatedFileName);
            trackActionsDone(ActionName.CreateFolder);
          } else {
            updateItem(findItem(id), { name: validatedFileName });
            trackActionsDone(ActionName.Rename);
          }

          selectItem(id);
          editItemComplete();
        },
      updateLabels:
        ({ updateItem }) =>
        (item, labels) =>
          updateItem(item, { labels }),
      searchByLabel:
        ({ navigateTo }) =>
        (label) =>
          navigateTo(RouteBuilder.Search(MainSource.All, label)),
      handleSearchEnterPressed:
        ({ source, folderId, isManageableMedia, isRecommended, navigateTo, isHome, isEnterprise, enterpriseTab }) =>
        (searchQuery) => {
          if (isManageableMedia || isRecommended || isHome) {
            navigateTo(RouteBuilder.Search(MainSource.All, searchQuery));
          } else if (isEnterprise && enterpriseTab) {
            const queryString = searchQuery
              ? `searchQuery=${encodeURIComponent(searchQuery)}&searchCacheKiller=${Date.now()}`
              : '';
            navigateTo(RouteBuilder.EnterpriseTab(enterpriseTab, folderId || '', queryString));
          } else {
            navigateTo(RouteBuilder.Search(source, searchQuery, folderId));
          }
        },
      handleSearchClearButtonClick:
        ({ source, folderId, navigateTo, isEnterprise, enterpriseTab }) =>
        () => {
          if (isEnterprise && enterpriseTab) {
            navigateTo(RouteBuilder.EnterpriseTab(enterpriseTab, folderId || ''));
          } else {
            navigateTo(RouteBuilder.Source(isAllSource(source) ? MainSource.SiteFiles : source, folderId));
          }
        },
      openItemInMediaStudioFromContextMenu:
        ({ selectedItems, editImage }) =>
        () =>
          editImage({ file: selectedItems[0], mode: MediaImageStudioMode.Transform }),
      handleFolderCreateInMoveToModal:
        ({ folderId, source, prependItems, trackFolderCreated }) =>
        (folder: MediaManagerSDKFolder) => {
          if (folder.parentId === folderId && folder.source === source) {
            prependItems([folder]);
          }
          trackFolderCreated(folder.id, ClickOrigin.MoveTo);
        },
      handleDropOnFolder:
        ({ isShowAll, moveItemsAndNotify, currentFolder }) =>
        (files, folder) => {
          if (isShowAll) {
            return;
          }

          if (currentFolder === folder) {
            return;
          }

          moveItemsAndNotify(files, folder);
        },
    }),
    withHandlers({
      handleJumpToNextItem:
        ({ jumpToNextItem }) =>
        ({ jumpTo, isInExpandedMode, isSingleItemSelected }) =>
        (event) => {
          if (isInExpandedMode && !isSingleItemSelected) {
            return;
          }

          jumpToNextItem({ withShift: Boolean(event && event.shiftKey), jumpTo });
        },
    }),
    withExpandedViewController(
      ({ trackActionsDone }) => ({
        onOpen: () => trackActionsDone(ActionName.Preview),
      }),
      ['trackActionsDone'],
    ),
    withHandlers({
      trackPreviewKeyPressed:
        ({ trackPreviewActions, isInExpandedMode, selectedItems }) =>
        () =>
          trackPreviewActions(
            {
              clickOrigin: PreviewClickOrigin.Keyboard,
              target: isInExpandedMode ? PreviewTarget.Opened : PreviewTarget.Closed,
            },
            selectedItems,
          ),
      trackNavigationKeyPressed:
        ({ trackItemsSelected, selectedItems, selectedItemTypes }) =>
        () =>
          trackItemsSelected(selectedItems, { clickOrigin: PreviewClickOrigin.Keyboard }, selectedItemTypes),
    }),
    withOpenedModalName,
    withHandlers({
      selectAllItemsHotKey: ({ openedModal, selectAllItems }) =>
        shouldKeepKeyBehaviorForModal(openedModal) ? noop : onHotKeys(`${Key.Ctrl}+${Key.A}`, selectAllItems),
      copyItemsHotKey: ({ trackActionsMenuClick, copySelectedItems }) =>
        onHotKeys(`${Key.Ctrl}+${Key.C}`, (...args) => {
          trackActionsMenuClick(ActionClickOrigin.Keyboard, ActionName.Copy);
          copySelectedItems(...args);
        }),
      cutItemsHotKey: ({ trackActionsMenuClick, cutSelectedItems }) =>
        onHotKeys(`${Key.Ctrl}+${Key.X}`, (...args) => {
          trackActionsMenuClick(ActionClickOrigin.Keyboard, ActionName.Cut);
          cutSelectedItems(...args);
        }),
      pasteItemsHotKey: ({ trackActionsMenuClick, pasteToCurrentFolder }) =>
        onHotKeys(`${Key.Ctrl}+${Key.V}`, (...args) => {
          trackActionsMenuClick(ActionClickOrigin.Keyboard, ActionName.Paste);
          pasteToCurrentFolder(...args);
        }),

      spaceHotKey: ({ openedModal, toggleExpandedView, trackPreviewKeyPressed, selectedItems }) =>
        isEmpty(selectedItems) || shouldKeepKeyBehaviorForModal(openedModal)
          ? noop
          : onHotKeys(Key.Space, memoizedOver(toggleExpandedView, trackPreviewKeyPressed)),

      jumpToNextItemHotKey: ({
        handleJumpToNextItem,
        isInExpandedMode,
        trackPreviewKeyPressed,
        trackNavigationKeyPressed,
        isSingleItemSelected,
        openedModal,
      }) =>
        shouldKeepKeyBehaviorForModal(openedModal)
          ? noop
          : onHotKeys(
              `${Key.Shift}+${Key.RightArrow}|${Key.RightArrow}`,
              memoizedOver(
                handleJumpToNextItem({ jumpTo: JumpTo.Next, isInExpandedMode, isSingleItemSelected }),
                isInExpandedMode ? trackPreviewKeyPressed : trackNavigationKeyPressed,
              ),
            ),

      jumpToPrevItemHotKey: ({
        handleJumpToNextItem,
        isInExpandedMode,
        trackPreviewKeyPressed,
        trackNavigationKeyPressed,
        isSingleItemSelected,
        openedModal,
      }) =>
        shouldKeepKeyBehaviorForModal(openedModal)
          ? noop
          : onHotKeys(
              `${Key.Shift}+${Key.LeftArrow}|${Key.LeftArrow}`,
              memoizedOver(
                handleJumpToNextItem({ jumpTo: JumpTo.Prev, isInExpandedMode, isSingleItemSelected }),
                isInExpandedMode ? trackPreviewKeyPressed : trackNavigationKeyPressed,
              ),
            ),

      jumpToBottomItemHotKey: ({
        handleJumpToNextItem,
        trackNavigationKeyPressed,
        isInExpandedMode,
        isSingleItemSelected,
        openedModal,
      }) =>
        isInExpandedMode || shouldKeepKeyBehaviorForModal(openedModal)
          ? noop
          : onHotKeys(
              `${Key.Shift}+${Key.DownArrow}|${Key.DownArrow}`,
              memoizedOver(
                handleJumpToNextItem({ jumpTo: JumpTo.Bottom, isInExpandedMode, isSingleItemSelected }),
                trackNavigationKeyPressed,
              ),
            ),

      jumpToTopItemHotKey: ({
        handleJumpToNextItem,
        trackNavigationKeyPressed,
        isInExpandedMode,
        isSingleItemSelected,
        openedModal,
      }) =>
        isInExpandedMode || shouldKeepKeyBehaviorForModal(openedModal)
          ? noop
          : onHotKeys(
              `${Key.Shift}+${Key.UpArrow}|${Key.UpArrow}`,
              memoizedOver(
                handleJumpToNextItem({ jumpTo: JumpTo.Top, isInExpandedMode, isSingleItemSelected }),
                trackNavigationKeyPressed,
              ),
            ),
    }),
    withGetItemPrice,
    withPurchaseMedia,
    withProps(
      ({
        selectedItems,
        hasSelectedItems,
        isSingleItemSelected,
        getItemPrice,
        getTaxSettings,
        areMultipleItemsSelected,
        mmgrSettings: {
          mediaType,
          mimeTypesAllowed = [],
          selection,
          canSubmitItems,
          canSubmitUntranscoded,
          canSubmitTransparentVideos,
          isLimitMimeTypesEnabled,
        },
        isShowAll,
        intl: { formatMessage, formatList },
        isShutterstock,
      }) => {
        const acceptedMediaTypes = [...deserializeStringToArray<MediaType>(mediaType)];
        const firstActiveItem = head(selectedItems);
        const isBuyButtonVisible = any(canItemBeBought, selectedItems);

        const isFileOfAnotherMediaType = any<MediaManagerSDKFile>(
          (file) => !contains(file.mediaType, acceptedMediaTypes),
          selectedItems,
        );
        const isTransparentVideoAndCantBeSubmitted =
          !canSubmitTransparentVideos && any(isTransparentVideoFile, selectedItems);

        const hasMimeTypesLimitation = isLimitMimeTypesEnabled && !isEmpty(mimeTypesAllowed);
        const includesFileOfInvalidMimeType =
          hasMimeTypesLimitation &&
          any<MediaManagerSDKFile>((file) => !contains(file.mimeType, mimeTypesAllowed), selectedItems);

        // TODO: check PublicMedia purchase
        const showButtonWithPrice =
          isShutterstock || (firstActiveItem && all(propEq('mediaType', firstActiveItem.mediaType), selectedItems));

        const buyButtonDisableTooltipTranslationKey =
          !showButtonWithPrice || (all(canItemBeBought, selectedItems) && areMultipleItemsSelected)
            ? 'files-manager.tooltips.multiple-paid-items-are-chosen'
            : isFileOfAnotherMediaType
            ? 'files-manager.tooltips.file-of-another-type'
            : '';

        let buyButtonText = <FormattedMessage id="shutterstock.item.buy.multiple" />;

        if (isBuyButtonVisible && showButtonWithPrice) {
          const price = getItemPrice(firstActiveItem);
          const tax = getTaxSettings(firstActiveItem);

          buyButtonText = isPictureFile(firstActiveItem) ? (
            <FormattedMessage id="shutterstock.buy-image" values={{ price }} />
          ) : (
            <FormattedMessage id="shutterstock.buy-video" values={{ price }} />
          );

          buyButtonText = (
            <PurchaseButtonWrapper displayTaxType={tax?.displayTaxType} showPricesWithTax={tax?.showPricesWithTax}>
              {buyButtonText}
            </PurchaseButtonWrapper>
          );
        }

        const chooseButtonDisableTooltipTranslationKey = !hasSelectedItems
          ? // no selected items
            'files-manager.tooltips.no-selected-items'
          : any(isFolder, selectedItems)
          ? // folder is selected
            'files-manager.tooltips.folder-is-chosen'
          : selection === Selection.Single && areMultipleItemsSelected
          ? // multiple items selected when one file is needed
            'files-manager.tooltips.single-file-required'
          : includesFileOfInvalidMimeType
          ? 'files-manager.tooltips.file-of-another-type'
          : isFileOfAnotherMediaType
          ? // e.g. choosing picture when video is needed
            'files-manager.tooltips.file-of-another-type'
          : isTransparentVideoAndCantBeSubmitted
          ? // e.g. choosing transparent video into player that doesn't support it
            'files-manager.tooltips.transparent-video-should-use-videobox'
          : (!canSubmitUntranscoded && any<MediaManagerSDKFile>(isTranscoding, selectedItems)) ||
            (canSubmitUntranscoded &&
              pipe<MediaManagerSDKFile[], MediaManagerSDKFile[], boolean>(
                filter(complement(isVideoFile)),
                any(isTranscoding),
              )(selectedItems))
          ? // file in transcoding state
            'files-manager.tooltips.file-is-transcoding'
          : any<MediaManagerSDKFile>(isFailed, selectedItems)
          ? // files transcoding failed
            'files-manager.tooltips.file-transcoding-failed'
          : null;

        return {
          chooseButtonDisableTooltipTranslationKey,
          acceptedMediaTypeTranslation: includesFileOfInvalidMimeType
            ? pipe(
                (mimeTypes) => mimeTypesToNames(mimeTypes),
                uniq,
                (mimeTypes) => formatList(mimeTypes, { type: 'disjunction' }),
              )(mimeTypesAllowed)
            : pipe(
                (mediaTypes) => mediaTypesArrayToTranslatedArray(mediaTypes, formatMessage),
                (mediaTypes) => formatList(mediaTypes, { type: 'disjunction' }),
              )(acceptedMediaTypes),
          isChooseButtonVisible: canSubmitItems && !isBuyButtonVisible && !isShowAll,
          isChooseButtonDisabled: Boolean(chooseButtonDisableTooltipTranslationKey),
          isBuyButtonDisabled: (!isSingleItemSelected && isBuyButtonVisible) || isFileOfAnotherMediaType,
          isBuyButtonVisible,
          buyButtonText,
          buyButtonDisableTooltipTranslationKey,
          showButtonWithPrice,
        };
      },
    ),
    ejectIntl,
    withStateHandlers(
      {
        isChooseTooltipShown: false,
      },
      {
        showChooseTooltip: () => () => ({ isChooseTooltipShown: true }),
        hideChooseTooltip: () => () => ({ isChooseTooltipShown: false }),
      },
    ),
    withHandlers({
      brieflyDisplayDisabledTooltip:
        ({ showChooseTooltip, hideChooseTooltip }) =>
        () => {
          showChooseTooltip();
          setTimeout(hideChooseTooltip, 2000);
        },
    }),
    withRouter,
    withHandlers({
      emitItems:
        ({ trackMediaManagerFileChosen, sortType, layout, location, folderId, mmgrSettings, source }) =>
        (items) => {
          trackMediaManagerFileChosen(items, ItemsSubmitType.Select);
          fedopsLogger.interactionEnded(FedOpsInteraction.SubmitFiles);

          parentConnection.emit(ParentConnectionEvent.ItemsSubmitted, {
            path: preparePathBeforeSubmit(replace(/^\//, '', location.pathname), location.search),
            layout,
            sort: sortType,
            items: prepareItemsBeforeSubmit(mmgrSettings, items),
            folderId: convertToSubmitFolderId(folderId, source),
          });
        },
    }),
    withSafeTimeout,
    withHandlers({
      submitItems:
        ({
          mmgrSettings,
          getPublicItemsInfo,
          isChooseButtonDisabled,
          brieflyDisplayDisabledTooltip,
          emitItems,
          startImport,
          stopImport,
          setSafeTimeout,
          selectedItems,
        }) =>
        () => {
          if (isChooseButtonDisabled) {
            brieflyDisplayDisabledTooltip();
            return;
          }

          fedopsLogger.interactionStarted(FedOpsInteraction.SubmitFiles);

          const {
            publicItems = [],
            unsplashItemsToImport = [],
            restItems = [],
          } = groupBy<MediaManagerSDKFile>(
            cond([
              [isMediaFromWixItem, always('publicItems')],
              [isUnsplashItem, always('unsplashItemsToImport')],
              [T, always('restItems')],
            ]),
            selectedItems,
          );

          const importUnsplash = (items: MediaManagerSDKFile[]) => {
            if (isEmpty(items)) {
              return Promise.resolve([]);
            }

            return Promise.all(map((item) => importUnsplashItem(item, mmgrSettings), items));
          };

          const getPublicInfo = (items: MediaManagerSDKFile[]): Promise<MediaManagerSDKFile[]> =>
            getPublicItemsInfo(pluck('id', publicItems))
              .then(
                reduce<MediaManagerSDKFile, { [key: string]: MediaManagerSDKFile }>(
                  (acc, publicItemToSubmit) => ({ ...acc, [publicItemToSubmit.id]: publicItemToSubmit }),
                  {},
                ),
              )
              .then((publicItemsInfo) =>
                map<MediaManagerSDKFile, MediaManagerSDKFile>((item) => publicItemsInfo[item.id] || item, items),
              );

          const clearSafeTimeout = setSafeTimeout(startImport, IMPORT_LOADER_DELAY);
          const finalize = () => {
            stopImport();
            clearSafeTimeout();
          };

          return Promise.all([importUnsplash(unsplashItemsToImport), getPublicInfo(publicItems), restItems])
            .then(unnest)
            .then(emitItems)
            .catch((reason) => {
              errorLogger.log(BI_ERRORS.general.chooseFile, {
                reason,
                p1: JSON.stringify(map(pick(['id', 'mediaType', 'name', 'fileUrl', 'source']), selectedItems)),
              });
            })
            .then(finalize);
        },
      buyItem: ({ purchaseItemFunctionFactory, trackItemsSubmittedAnalyticsCommonProps, storageQuotaAwareHandler }) =>
        storageQuotaAwareHandler(
          purchaseItemFunctionFactory(trackItemsSubmittedAnalyticsCommonProps),
          StorageQuotaUseCase.SHUTTERSTOCK_PURCHASE,
        ),
    }),
    withoutRouter,
    withScrollStream,
    withHandlers({
      buySelectedItem:
        ({ selectedItems, buyItem }) =>
        () => {
          if (selectedItems.length !== 1) {
            throw new Error('Cannot buy more than 1 item at a time');
          }

          const item = head(selectedItems);

          buyItem(item);
        },
      handleDropToTrash:
        ({ moveItemsToTrash }) =>
        (items) =>
          moveItemsToTrash(items),
      handleItemLoaded:
        ({
          folderId,
          source,
          mediaTypes,
          insertAfterFolders,
          emitScroll,
          setCanScrollToUploadedItem,
          canScrollToUploadedItem,
        }) =>
        (data) => {
          const itemOrArrayOfItems = getItemFromServerResponse(data);
          const item = isArray(itemOrArrayOfItems) ? head(itemOrArrayOfItems) : itemOrArrayOfItems;

          const isFileAndCanBeInserted =
            isFile(item) && item.folderId === folderId && isMediaTypeEnabled(item.mediaType, mediaTypes);
          const isFolderAndCanBeInserted = isFolder(item) && item.parentId === folderId;
          const canItemBeInserted = isFileAndCanBeInserted || isFolderAndCanBeInserted;

          if (canItemBeInserted && item.source === source) {
            insertAfterFolders([item]);

            if (canScrollToUploadedItem) {
              setCanScrollToUploadedItem(false);
              setTimeout(() => {
                emitScroll(item.id);
              }, 0);
            }
          }
        },
    }),
    withHandlers({
      handleClickChooseFilesDisabled:
        ({ selectedItems, trackMediaManagerFileChosenTry }) =>
        () => {
          trackMediaManagerFileChosenTry(selectedItems);
        },
      handleFileDoubleClick: ({ selectedItems, buySelectedItem, submitItems }) =>
        canItemBeBought(selectedItems[0]) ? buySelectedItem : submitItems,
      handleDropToSidebar:
        ({ handleDropToTrash }) =>
        ({ target, items }) => {
          switch (target) {
            case MainSource.Trash:
              return handleDropToTrash(items);

            default:
              return;
          }
        },
      handleFolderEnterKeyClick:
        ({ openFolder, selectedItems }) =>
        () => {
          openFolder(selectedItems[0]);
        },
    }),
    withHandlers({
      enterHotKey: ({
        openedModal,
        isSingleFolderSelected,
        handleFolderEnterKeyClick,
        isChooseButtonDisabled,
        submitItems,
        isChooseButtonVisible,
        handleClickChooseFilesDisabled,
        buySelectedItem,
        isBuyButtonVisible,
        isBuyButtonDisabled,
      }) => {
        if (shouldKeepKeyBehaviorForModal(openedModal)) {
          return noop;
        }

        const key = Key.Enter;

        if (isSingleFolderSelected) {
          return onHotKeys(key, handleFolderEnterKeyClick);
        }

        const handler =
          isBuyButtonVisible && !isBuyButtonDisabled
            ? buySelectedItem
            : isChooseButtonVisible && !isChooseButtonDisabled
            ? submitItems
            : handleClickChooseFilesDisabled;
        return onHotKeys(key, handler, () => handler !== handleClickChooseFilesDisabled);
      },
    }),
    withoutMMGRSettings,
    withPropsOnChange(
      [
        'isChooseButtonVisible',
        'isChooseButtonDisabled',
        'isBuyButtonVisible',
        'isBuyButtonDisabled',
        'buyButtonText',
        'chooseButtonDisableTooltipTranslationKey',
        'acceptedMediaTypeTranslation',
        'selectedItemsCount',
        'buyButtonDisableTooltipTranslationKey',
        'footerDisclaimer',
        'isImportInProgress',
        'isChooseTooltipShown',
      ],
      ({
        isChooseButtonVisible,
        isChooseButtonDisabled,
        isBuyButtonVisible,
        isBuyButtonDisabled,
        buyButtonText,
        chooseButtonDisableTooltipTranslationKey,
        acceptedMediaTypeTranslation,
        selectedItemsCount,
        buyButtonDisableTooltipTranslationKey,
        footerDisclaimer,
        isImportInProgress,
        isChooseTooltipShown,
        buySelectedItem,
        submitItems,
        handleClickChooseFilesDisabled,
        showChooseTooltip,
        hideChooseTooltip,
      }) => {
        const tinyLoader = (
          <span>
            <Loader size="tiny" />
          </span>
        );
        const isAnyButtonDisabled = isChooseButtonDisabled || isBuyButtonDisabled;
        const handleClickChooseFilesDisabledButton = isAnyButtonDisabled ? handleClickChooseFilesDisabled : noop;

        let popoverContent = null;
        let button = null;

        if (isChooseButtonVisible) {
          if (isChooseButtonDisabled) {
            popoverContent = (
              <FormattedMessage
                id={chooseButtonDisableTooltipTranslationKey}
                values={{ mediaType: acceptedMediaTypeTranslation }}
              />
            );
          }

          button = (
            <Button
              onClick={submitItems}
              size="small"
              disabled={isChooseButtonDisabled}
              prefixIcon={isImportInProgress ? tinyLoader : null}
              dataHook="select-items"
            >
              <FormattedMessage id="actions.choose-files" values={{ filesCount: selectedItemsCount }} />
            </Button>
          );
        }

        if (isBuyButtonVisible) {
          if (isBuyButtonDisabled) {
            popoverContent = (
              <FormattedMessage
                id={buyButtonDisableTooltipTranslationKey}
                values={{ mediaType: acceptedMediaTypeTranslation }}
              />
            );
          }

          button = (
            <Button
              onClick={buySelectedItem}
              size="small"
              disabled={isBuyButtonDisabled}
              dataHook="purchase-media-footer-button"
            >
              {buyButtonText}
            </Button>
          );
        }

        return {
          footerContent: (
            <>
              {footerDisclaimer && <span className={styles.footerDisclaimer}>{footerDisclaimer}</span>}
              <Popover
                shown={isChooseTooltipShown && isAnyButtonDisabled}
                skin="dark"
                showArrow
                placement="top"
                showDelay={0}
                appendTo="viewport"
                dataHook="submit-button-tooltip"
              >
                <Popover.Content>
                  <Text weight="normal" size="small" light className={styles.chooseButtonTooltipContentWrapper}>
                    {popoverContent}
                  </Text>
                </Popover.Content>

                <Popover.Element>
                  <span
                    style={{ display: 'inline-flex' }}
                    onClick={handleClickChooseFilesDisabledButton}
                    onMouseEnter={showChooseTooltip}
                    onMouseLeave={hideChooseTooltip}
                  >
                    {button}
                  </span>
                </Popover.Element>
              </Popover>
            </>
          ),
        };
      },
    ),
    withUploadQueueObservables,
    withDropOnSidebar,
    withLifecycle(() => {
      let itemUploadedSubscription: Subscription;
      let folderUploadedSubscription: Subscription;
      let dropOnSidebarSubscription: Subscription;
      let queueFinishedSubscription: Subscription;
      let queueStartSubscription: Subscription;
      let resetFilesStreamSubscription: Subscription;

      return {
        onDidMount: ({
          selectAllItemsHotKey,
          jumpToNextItemHotKey,
          jumpToPrevItemHotKey,
          jumpToBottomItemHotKey,
          jumpToTopItemHotKey,
          spaceHotKey,
          copyItemsHotKey,
          cutItemsHotKey,
          pasteItemsHotKey,
          itemUploadedObservable,
          folderUploadedObservable,
          handleItemLoaded,
          dropOnSidebarObservable,
          handleDropToSidebar,
          queueFinishedObservable,
          handleUploadQueueFinished,
          handleUploadQueueStart,
          queueStartObservable,
          resetFilesStream,
          loadMoreItems,
          enterHotKey,
        }) => {
          if (!contains(PreventSubscribeEventsNames.JumpToNextItemHotKey, preventSubscribeEvents)) {
            window.addEventListener('keydown', jumpToNextItemHotKey, false);
          }

          if (!contains(PreventSubscribeEventsNames.JumpToPrevItemHotKey, preventSubscribeEvents)) {
            window.addEventListener('keydown', jumpToPrevItemHotKey, false);
          }

          if (!contains(PreventSubscribeEventsNames.JumpToBottomItemHotKey, preventSubscribeEvents)) {
            window.addEventListener('keydown', jumpToBottomItemHotKey, false);
          }

          if (!contains(PreventSubscribeEventsNames.JumpToTopItemHotKey, preventSubscribeEvents)) {
            window.addEventListener('keydown', jumpToTopItemHotKey, false);
          }

          if (!contains(PreventSubscribeEventsNames.SelectAllItemsHotKey, preventSubscribeEvents)) {
            window.addEventListener('keydown', selectAllItemsHotKey, false);
          }

          window.addEventListener('keydown', pasteItemsHotKey, false);
          window.addEventListener('keydown', copyItemsHotKey, false);
          window.addEventListener('keydown', cutItemsHotKey, false);

          window.addEventListener('keydown', spaceHotKey, false);

          window.addEventListener('keydown', enterHotKey, false);

          itemUploadedSubscription = itemUploadedObservable.subscribe(handleItemLoaded);
          folderUploadedSubscription = folderUploadedObservable.subscribe(handleItemLoaded);
          dropOnSidebarSubscription = dropOnSidebarObservable.subscribe(handleDropToSidebar);
          queueFinishedSubscription = queueFinishedObservable.subscribe(handleUploadQueueFinished);
          queueStartSubscription = queueStartObservable.subscribe(handleUploadQueueStart);

          if (resetFilesStream) {
            resetFilesStreamSubscription = resetFilesStream.subscribe(() => {
              requestAnimationFrame(loadMoreItems);
            });
          }
        },
        onWillUnmount: ({
          selectAllItemsHotKey,
          jumpToNextItemHotKey,
          jumpToPrevItemHotKey,
          jumpToBottomItemHotKey,
          jumpToTopItemHotKey,
          spaceHotKey,
          copyItemsHotKey,
          cutItemsHotKey,
          pasteItemsHotKey,
          enterHotKey,
        }) => {
          window.removeEventListener('keydown', pasteItemsHotKey, false);
          window.removeEventListener('keydown', copyItemsHotKey, false);
          window.removeEventListener('keydown', cutItemsHotKey, false);
          window.removeEventListener('keydown', selectAllItemsHotKey, false);
          window.removeEventListener('keydown', jumpToNextItemHotKey, false);
          window.removeEventListener('keydown', jumpToPrevItemHotKey, false);
          window.removeEventListener('keydown', spaceHotKey, false);
          window.removeEventListener('keydown', jumpToBottomItemHotKey, false);
          window.removeEventListener('keydown', jumpToTopItemHotKey, false);
          window.removeEventListener('keydown', enterHotKey, false);

          itemUploadedSubscription.unsubscribe();
          folderUploadedSubscription.unsubscribe();
          dropOnSidebarSubscription.unsubscribe();
          queueFinishedSubscription.unsubscribe();
          queueStartSubscription.unsubscribe();

          if (resetFilesStreamSubscription) {
            resetFilesStreamSubscription.unsubscribe();
          }
        },
      };
    }),
  );
